import React from 'react';
import Select from 'react-select';
import dbSupportedLanguages from './../../data/dbSupportedLanguages';
import dbBodyColors from './../../data/dbBodyColors';
import dbBodyFonts from './../../data/dbBodyFonts';
import dbBodyStyles from '../../data/dbBodyStyles';
import dbSubjectStyles from './../../data/dbSubjectStyles';
import dbTranslationEngines from '../../data/dbTranslationEngines';
import dbBrandIcons from './../../data/dbBrandIcons';


const ComRuleEditor = ({
    initialData,
    activeDomains,
    onDataChange,
    isDemo = false,
}) => {
    const _FIX_RATE = 3.0;
    const [form, setForm] = React.useState({
        ruleName: initialData?.ruleName || '',
        domainFrom: (initialData?.domainFrom && (activeDomains.includes(initialData.domainFrom) || initialData.domainFrom === 'all'))
            ? initialData.domainFrom
            : 'all',
        subjectStyle: (initialData?.subjectStyle && dbSubjectStyles.some(style => style.key === initialData.subjectStyle))
            ? initialData.subjectStyle
            : dbSubjectStyles[0].key,
        translationLanguage: (initialData?.translationLanguage && dbSupportedLanguages.some(lang => lang.key === initialData.translationLanguage))
            ? initialData.translationLanguage
            : "fr-CA",
        translationEngine: (initialData?.translationEngine && dbTranslationEngines.some(engine => engine.key === initialData.translationEngine))
            ? initialData.translationEngine
            : "gpt-4.1-mini",
        bodyStyle: (initialData?.bodyStyle && dbBodyStyles.some(style => style.key === initialData.bodyStyle))
            ? initialData.bodyStyle
            : dbBodyStyles[0].key,
        bodyColor: (initialData?.bodyColor && dbBodyColors.some(color => color.key === initialData.bodyColor))
            ? initialData.bodyColor
            : dbBodyColors[0].key,
        bodyFont: (initialData?.bodyFont && dbBodyFonts.some(font => font.key === initialData.bodyFont))
            ? initialData.bodyFont
            : dbBodyFonts[0].key,
    });
    const demoOriginalSubject = dbSupportedLanguages.find(lang => lang.key === "en-US").demoSubject || "Original Subject";
    const demoTranslatedSubject = dbSupportedLanguages.find(lang => lang.key === form.translationLanguage).demoSubject || "Translated Subject";
    const demoOriginalBody = dbSupportedLanguages.find(lang => lang.key === "en-US").demoBody || "Original Body";
    const demoTranslatedBody = dbSupportedLanguages.find(lang => lang.key === form.translationLanguage).demoBody || "Translated Body";
    const isMobile = (window.innerWidth < 768);
    const commonSelectProps = {
        getOptionLabel: (e) => e.value,
        getOptionValue: (e) => e.key,
        isSearchable: !isMobile,
        isClearable: false,
        filterOption: (option, inputValue) => {
            const { value, key } = option.data;
            return (
                value.toLowerCase().includes(inputValue.toLowerCase()) ||
                key.toLowerCase().includes(inputValue.toLowerCase())
            );
        },
    };

    React.useEffect(() => {
        onDataChange && onDataChange(form);
    }, [form]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    const domainOptions = [
        { key: 'all', value: 'All Domains' },
        ...activeDomains.map(d => ({ key: d, value: d })),
    ];

    return <div className="text-start">
        {(isDemo === false) && <p className="mb-3 text-center">You can edit the rule settings below. The changes will be reflected in the preview panel on the right.</p>}
        <div className="row mb-2">
            <div className="col-12 col-md-6 fs-7 mb-3">
                {(isDemo === false) &&
                    <div className="row align-items-center mb-2">
                        <label className="col-3 col-form-label">Rule Name:</label>
                        <div className="col-9">
                            <input
                                className="form-control form-control-sm"
                                placeholder="Enter rule name"
                                id="ruleName"
                                name="ruleName"
                                defaultValue={form.ruleName}
                                onChange={handleChange}
                            />
                        </div>
                    </div>}

                <div className="row align-items-center mb-2">
                    <label className="col-3 col-form-label">From Domain:</label>
                    <div className="col-9">
                        <Select
                            {...commonSelectProps}
                            id="domainFrom"
                            name="domainFrom"
                            value={domainOptions.find(o => o.key === form.domainFrom)}
                            onChange={(selected) => handleChange({ target: { name: 'domainFrom', value: selected.key } })}
                            options={domainOptions}
                        />
                    </div>
                </div>

                <div className="row align-items-center mb-2">
                    <label className="col-3 col-form-label">Subject Style:</label>
                    <div className="col-9">
                        <Select
                            {...commonSelectProps}
                            id="subjectStyle"
                            name="subjectStyle"
                            value={dbSubjectStyles.find(style => style.key === form.subjectStyle)}
                            onChange={(selected) => handleChange({ target: { name: 'subjectStyle', value: selected.key } })}
                            options={dbSubjectStyles}
                        />
                    </div>
                </div>

                <div className="row align-items-center mb-2">
                    <label className="col-3 col-form-label">Translate To:</label>
                    <div className="col-9">
                        <Select
                            {...commonSelectProps}
                            id="translationLanguage"
                            name="translationLanguage"
                            value={dbSupportedLanguages.find(lang => lang.key === form.translationLanguage)}
                            onChange={(selected) => handleChange({ target: { name: 'translationLanguage', value: selected.key } })}
                            options={[
                                { options: dbSupportedLanguages.filter(lang => lang.isBidirectional), label: "Smart Bidirectional Translation", },
                                { options: dbSupportedLanguages.filter(lang => !lang.isBidirectional), label: "Target Languages", }
                            ]}
                            getOptionLabel={(e) => (
                                <div className="d-flex align-items-center">
                                    {e.isBidirectional && <>
                                        <img className="me-2" width="20" alt={e.value1} src={`https://flagcdn.com/${e.flag1}.svg`} />
                                        {e.value1}{" <=> "}
                                        <img className="mx-2" width="20" alt={e.value2} src={`https://flagcdn.com/${e.flag2}.svg`} />
                                        {e.value2}
                                    </>}
                                    {e.flag && <>
                                        <img className="me-2" width="20" alt={e.value} src={`https://flagcdn.com/${e.flag}.svg`} />
                                        {e.value}
                                    </>}
                                </div>
                            )}
                            maxMenuHeight={220}
                        />
                    </div>
                </div>

                <div className="row align-items-center mb-2">
                    <label className="col-3 col-form-label">AI Engine:</label>
                    <div className="col-9">
                        <Select
                            {...commonSelectProps}
                            id="translationEngine"
                            name="translationEngine"
                            value={dbTranslationEngines.find(engine => engine.key === form.translationEngine)}
                            onChange={(selected) => handleChange({ target: { name: 'translationEngine', value: selected.key } })}
                            options={dbTranslationEngines}
                            getOptionLabel={(e) => (
                                <div className="d-flex align-items-center">
                                    <span style={{ width: '16px', height: '16px', lineHeight: 0, }} className="me-2">
                                        {dbBrandIcons.find(svg => svg.key === e.brand)?.value}
                                    </span>
                                    {e.value}
                                    <span className="ms-auto text-muted fs-8">
                                        ${_FIX_RATE * e.price / 1000}/{e.priceUnit}
                                    </span>
                                </div>
                            )}
                            maxMenuHeight={220}
                        />
                    </div>
                </div>

                <div className="row align-items-center mb-2">
                    <label className="col-3 col-form-label">Layout Style:</label>
                    <div className="col-9">
                        <Select
                            {...commonSelectProps}
                            id="bodyStyle"
                            name="bodyStyle"
                            value={dbBodyStyles.find(style => style.key === form.bodyStyle)}
                            onChange={(selected) => handleChange({ target: { name: 'bodyStyle', value: selected.key } })}
                            options={dbBodyStyles}
                        />
                    </div>
                </div>

                <div className="row align-items-center mb-2">
                    <label className="col-3 col-form-label">Color:</label>
                    <div className="col-9">
                        <Select
                            {...commonSelectProps}
                            id="bodyColor"
                            name="bodyColor"
                            value={dbBodyColors.find(color => color.key === form.bodyColor)}
                            onChange={(selected) => handleChange({ target: { name: 'bodyColor', value: selected.key } })}
                            options={dbBodyColors}
                            getOptionLabel={(e) => (
                                <div className="d-flex align-items-center">
                                    <span className="me-2" style={{ backgroundColor: e.color, width: '20px', height: '20px', borderRadius: '50%' }}></span>
                                    {e.value}</div>
                            )}
                        />
                    </div>
                </div>

                <div className="row align-items-center mb-2">
                    <label className="col-3 col-form-label">Font:</label>
                    <div className="col-9">
                        <Select
                            {...commonSelectProps}
                            id="bodyFont"
                            name="bodyFont"
                            value={dbBodyFonts.find(font => font.key === form.bodyFont)}
                            onChange={(selected) => handleChange({ target: { name: 'bodyFont', value: selected.key } })}
                            options={dbBodyFonts}
                            getOptionLabel={(e) => (
                                <div className="d-flex align-items-center">
                                    <span className="me-2" style={{ fontFamily: e.font }}>{e.value}</span>
                                </div>
                            )}
                            maxMenuHeight={isDemo ? 160 : 90}
                        />
                    </div>
                </div>
            </div>
            <div className="col-12 col-md-6">
                {/* Email preview panel - start */}
                <div className="card shadow-sm border border-1 border-secondary rounded-top fs-7" style={{ marginBottom: '60px' }}>
                    <div className="card-header bg-secondary fw-bold py-2 px-3 text-white text-center fs-6">
                        Email Preview{(form.ruleName) && ` - ${form.ruleName}`}
                    </div>
                    <div className="card-body p-3 bg-white">
                        <div className="input-group mb-3">
                            <span className="input-group-text fs-7">From:</span>
                            <span className="form-control fs-7">sender@{form.domainFrom === 'all' ? 'yourdomain.com' : form.domainFrom}</span>
                        </div>
                        <div className="input-group mb-3">
                            <span className="input-group-text fs-7">Subject:</span>
                            <span className="form-control fs-7">
                                {{
                                    originalDashTranslated: `${demoOriginalSubject} - ${demoTranslatedSubject}`,
                                    translatedDashOriginal: `${demoTranslatedSubject} - ${demoOriginalSubject}`,
                                    original: demoOriginalSubject,
                                    translated: demoTranslatedSubject,
                                }[form.subjectStyle]}
                            </span>
                        </div>
                        <hr />
                        <div
                            className="mb-3 d-flex flex-column gap-4"
                            style={{ whiteSpace: 'pre-wrap' }}
                        >
                            {(form.bodyStyle === 'originalThenTranslated') &&
                                <div className="text-muted fs-7">{demoOriginalBody}</div>}
                            <div className="fs-7"
                                style={{
                                    backgroundColor: dbBodyColors.find(color => color.key === form.bodyColor).color,
                                    fontFamily: dbBodyFonts.find(font => font.key === form.bodyFont).font,
                                }}>
                                {demoTranslatedBody}
                            </div>
                            {(form.bodyStyle === 'translatedThenOriginal') &&
                                <div className="text-muted fs-7">{demoOriginalBody}</div>}
                        </div>
                    </div>
                </div>
                {/* Email preview panel - end*/}
            </div>
        </div >
    </div >
}
export default ComRuleEditor;